<template>
    <section
        :id="block.id"
        :data-theme="colorTheme"
        :data-mode="mode"
        class="rounded-card-md relative w-full"
        :class="[
            layoutHeight,
            layoutContentAlignment,
            layoutPadding,
            {
                'surface-primary': showBgColor,
                isolate: blockSettings?.BackgroundElement?.length > 0,
            },
        ]"
        :style="customLayoutHeight"
    >
        <component
            :is="blockComponent"
            v-if="block.canonicalUid"
            :id="block.id"
            :block="blockSettings"
            :is-singleton="isSingleton"
        />
        <BlocksBackgroundElement
            v-if="blockSettings?.BackgroundElement?.length > 0"
            class="-z-10"
            :entry="blockSettings?.BackgroundElement[0]"
        />
    </section>
</template>

<script setup>
    const props = defineProps({
        block: Object,
        parentHasBgElement: Number,
        isSingleton: Boolean,
        isNewsletterArticle: Boolean,
    })

    const componentName = computed(() => {
        let name = props.block.__typename

        // remove _ds and _Entry from the name
        name = replace(name, 'ds_', '')
        name = replace(name, '_Entry', '')

        return name
    })

    const blockSettings = computed(() => {
        return mapKeys(props.block, (value, key) => {
            // remove prefix from key e.g. table_BgTitleOnly => BgTitleOnly
            return replace(key, /.*_/, '')
        })
    })

    const blockModules = import.meta.glob('./**/block.async.vue')

    const blockComponent = defineAsyncComponent({
        loader: () => {
            const block = blockModules[`./${upperFirst(componentName.value)}/block.async.vue`]
            return block ? block() : Promise.reject(new Error(`Unknown layout block component: ${componentName.value}`))
        },
    })

    const colorTheme = computed(() => {
        return blockSettings.value.ColorTheme
    })

    const mode = computed(() => {
        switch (blockSettings.value.ColorPalette) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return blockSettings.value.ColorPalette
        }
    })

    const showBgColor = computed(() => {
        const excludedComponents = ['TileLayoutBlock', 'NewsLayoutBlock', 'EventsLayoutBlock']

        // never show background color for these components
        if (excludedComponents.includes(componentName.value)) {
            return false
        }

        // If there's a background element, only show if pallete is not set to inherit ('')
        if (props.parentHasBgElement) {
            return !isEmpty(blockSettings.value.ColorPalette)
        }

        return true
    })

    const layoutHeight = computed(() => {
        switch (blockSettings.value?.Height) {
            case 'auto':
            case 'custom':
            default:
                return 'h-full'
            case 'portrait':
                return 'min-h-[320px] max-lg:aspect-square lg:h-[360px]'
            case 'square':
                return 'h-auto aspect-square'
        }
    })

    const customLayoutHeight = computed(() => {
        if (blockSettings.value?.Height !== 'custom') {
            return ''
        }

        return {
            height: `${blockSettings.value?.CustomHeight}px`,
        }
    })

    const layoutContentAlignment = computed(() => {
        switch (blockSettings.value?.ContentAlignment) {
            case 'center':
            default:
                return 'place-content-center'
            case 'top':
                return 'place-content-start'
            case 'bottom':
                return 'place-content-end'
        }
    })

    const layoutPadding = computed(() => {
        switch (blockSettings.value?.Padding) {
            case 'none':
            default:
                return ''
            case 'xs':
                return 'p-4'
            case 'sm':
                return 'p-6'
            case 'md':
                return 'p-[30px] lg:p-10'
            case 'lg':
                return 'p-10 lg:p-[60px]'
            case 'xl':
                return 'p-[50px] lg:p-20'
        }
    })
</script>
